import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Stack, Typography, Button, Card, Alert,
  TextField, InputAdornment, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';

import { useAuth } from '../../context/AuthContext';
import { usePlanillas, useDeleteDDJJ, usePagosNoDefinitivosDDJJ } from '../../hooks/useDDJJ';
import { mapSupabaseError } from '../../utils/errores';
import { formatCurrency, formatDate } from '../../utils/format';
import {
  type PlanillaDDJJ,
  type DDJJEstado,
  getEstadoLabel,
  getEstadoColor,
} from '../../types/ddjj';
import NuevaDDJJDialog from './NuevaDDJJDialog';

// ── Component ─────────────────────────────────────────────────────────────────

export default function DDJJList() {
  const navigate = useNavigate();
  const { effectiveRole, effectiveEmpresaId } = useAuth();
  const isAdminEmpresa = effectiveRole === 'admin_empresa';

  // Si es admin_empresa, filtra por su empresa
  const { data: planillas = [], isError, isLoading } = usePlanillas(
    isAdminEmpresa ? (effectiveEmpresaId ?? undefined) : undefined
  );

  const deleteDDJJ = useDeleteDDJJ();

  const [busqueda, setBusqueda] = useState('');
  const [nuevaDDJJOpen, setNuevaDDJJOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PlanillaDDJJ | null>(null);
  // Pagos pendientes que se rechazarían al eliminar (ADR #044)
  const { data: pagosPorRechazar = 0 } = usePagosNoDefinitivosDDJJ(deleteTarget?.id);

  // ── Filtro cliente ────────────────────────────────────────────────────────

  const filtered = planillas.filter((p) => {
    if (!busqueda.trim()) return true;
    const q = busqueda.toLowerCase();
    const razonSocial = p.empresa?.razon_social?.toLowerCase() ?? '';
    const periodo = p.periodo?.periodo?.toLowerCase() ?? '';
    return razonSocial.includes(q) || periodo.includes(q);
  });

  // ── Confirm delete ────────────────────────────────────────────────────────

  const cerrarEliminar = () => {
    setDeleteTarget(null);
    deleteDDJJ.reset();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDDJJ.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch {
      // se muestra en el diálogo vía deleteDDJJ.error (ticket #699: antes no pasaba nada)
    }
  };

  // ── Columns ───────────────────────────────────────────────────────────────

  const columns: GridColDef<PlanillaDDJJ>[] = [
    ...(!isAdminEmpresa
      ? [
          {
            field: 'empresa',
            headerName: 'Empresa',
            flex: 2,
            minWidth: 200,
            renderCell: (params: GridRenderCellParams<PlanillaDDJJ>) =>
              params.row.empresa?.razon_social ?? '-',
          } as GridColDef<PlanillaDDJJ>,
        ]
      : []),
    {
      field: 'periodo',
      headerName: 'Período',
      width: 130,
      renderCell: (params: GridRenderCellParams<PlanillaDDJJ>) => {
        const periodo = params.row.periodo?.periodo ?? '-';
        const numero = params.row.numero ?? 1;
        return (
          <Stack direction="column" spacing={0}>
            <Typography variant="body2">{periodo}</Typography>
            {numero > 1 && (
              <Typography variant="caption" color="text.secondary">
                DDJJ #{numero}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 130,
      renderCell: (params: GridRenderCellParams<PlanillaDDJJ, DDJJEstado>) => (
        <Chip
          label={getEstadoLabel(params.row.estado)}
          color={getEstadoColor(params.row.estado)}
          size="small"
        />
      ),
    },
    {
      field: 'monto_total',
      headerName: 'Total',
      width: 150,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridRenderCellParams<PlanillaDDJJ>) => (
        <Typography variant="body2">{formatCurrency(params.row.monto_total)}</Typography>
      ),
    },
    {
      field: 'fecha_carga',
      headerName: 'Fecha carga',
      width: 130,
      renderCell: (params: GridRenderCellParams<PlanillaDDJJ>) => (
        <Typography variant="body2">{formatDate(params.row.fecha_carga)}</Typography>
      ),
    },
    {
      field: 'acciones',
      headerName: '',
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams<PlanillaDDJJ>) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Ver detalle">
            <IconButton
              size="small"
              onClick={() => navigate(`/ddjj/${params.row.id}`)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.estado === 'borrador' && (
            <Tooltip title="Eliminar borrador">
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteTarget(params.row)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" fontWeight={600}>
          Declaraciones Juradas
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setNuevaDDJJOpen(true)}
        >
          Nueva DDJJ
        </Button>
      </Stack>

      {/* Filtro */}
      <TextField
        size="small"
        placeholder="Buscar por empresa o período…"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        sx={{ mb: 2, width: 320 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {/* Error */}
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar las declaraciones juradas.
        </Alert>
      )}

      {/* Grid */}
      <Card>
        <DataGrid
          rows={filtered}
          columns={columns}
          loading={isLoading}
          autoHeight
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          disableRowSelectionOnClick
          sx={{ border: 0 }}
        />
      </Card>

      {/* Dialog: Nueva DDJJ */}
      <NuevaDDJJDialog
        open={nuevaDDJJOpen}
        onClose={() => setNuevaDDJJOpen(false)}
        onCreated={(id) => {
          setNuevaDDJJOpen(false);
          navigate(`/ddjj/${id}`);
        }}
      />

      {/* Dialog: Confirmar eliminación */}
      <Dialog
        open={!!deleteTarget}
        onClose={cerrarEliminar}
        maxWidth="xs"
      >
        <DialogTitle>Eliminar borrador</DialogTitle>
        <DialogContent>
          ¿Confirmar la eliminación de la DDJJ borrador para{' '}
          <strong>{deleteTarget?.empresa?.razon_social}</strong> —{' '}
          período {deleteTarget?.periodo?.periodo}?
          {pagosPorRechazar > 0 && (
            <Alert severity="warning" sx={{ mt: 2, fontSize: 13 }}>
              Hay <strong>{pagosPorRechazar} pago(s) pendiente(s)</strong> sobre esta DDJJ: se{' '}
              <strong>rechazarán automáticamente</strong> al eliminarla.
            </Alert>
          )}
          {deleteDDJJ.isError && (
            <Alert severity="error" sx={{ mt: 2, fontSize: 13 }}>
              {mapSupabaseError(deleteDDJJ.error, 'No se pudo eliminar la DDJJ. Volvé a intentar; si persiste, avisá al administrador.')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={cerrarEliminar}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteDDJJ.isPending}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
